import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download, Loader } from 'lucide-react';
import { config } from '../utils/config';

declare global {
  interface Window {
    pdfjsLib: any;
  }
}

// Interface for container element
interface PDFContainer extends HTMLDivElement {
  pdfDoc?: any;
}

interface PDFViewerProps {
  documentId: string | number;
  filename?: string;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ documentId, filename }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.2);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<PDFContainer>(null);

  // Load the PDF.js library dynamically
  useEffect(() => {
    const loadPdfJs = async () => {
      // We'll use the CDN version for simplicity
      const pdfJsScript = document.createElement('script');
      pdfJsScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.min.js';
      // Remove integrity attribute
      pdfJsScript.crossOrigin = 'anonymous';
      pdfJsScript.async = true;
      
      document.body.appendChild(pdfJsScript);
      
      // Store the script element to remove it later
      const scriptElement = pdfJsScript;
      let workerScriptElement: HTMLScriptElement | null = null;
      
      pdfJsScript.onload = () => {
        // Initialize PDF.js worker
        const workerScript = document.createElement('script');
        workerScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js';
        // Remove integrity attribute
        workerScript.crossOrigin = 'anonymous';
        workerScript.async = true;
        document.body.appendChild(workerScript);
        
        // Store the worker script element to remove it later
        workerScriptElement = workerScript;
        
        // Set worker location
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = workerScript.src;
        
        // Load the PDF
        loadPdf();
      };
      
      // Return cleanup function
      return () => {
        // Check if elements exist before removing
        if (document.body.contains(scriptElement)) {
          document.body.removeChild(scriptElement);
        }
        
        if (workerScriptElement && document.body.contains(workerScriptElement)) {
          document.body.removeChild(workerScriptElement);
        }
      };
    };
    
    const cleanup = loadPdfJs();
    return () => {
      cleanup.then(cleanupFn => cleanupFn());
    };
  }, []);
  
    // Load PDF when documentId changes
    useEffect(() => {
      if (window.pdfjsLib && documentId) {
        loadPdf();
      }
    }, [documentId]);
  
    // Render page when page number or scale changes
    useEffect(() => {
      if (window.pdfjsLib && !loading) {
        renderPage();
      }
    }, [pageNum, scale, loading]);
  
    const loadPdf = async () => {
      if (!window.pdfjsLib) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch the PDF through your API
        const token = localStorage.getItem('token');
        const response = await fetch(`${config.API_URL}/api/documents/${documentId}/file`, {
          headers: {
            'Authorization': `Bearer ${token || ''}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch PDF');
        }
        
        const pdfData = await response.arrayBuffer();
        
        // Load the PDF document
        const loadingTask = window.pdfjsLib.getDocument({ data: pdfData });
        const pdf = await loadingTask.promise;
        
        // Store the PDF document in a ref (with null checking)
        if (containerRef.current) {
          containerRef.current.pdfDoc = pdf;
          setNumPages(pdf.numPages);
        }
        
        // Render the first page
        setPageNum(1);
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError(err instanceof Error ? err.message : 'Failed to load PDF');
        setLoading(false);
      }
    };
  
    const renderPage = async () => {
      // Safely check for all required references
      const container = containerRef.current;
      const canvas = canvasRef.current;
      
      if (!container?.pdfDoc || !canvas) return;
      
      try {
        const pdfDoc = container.pdfDoc;
        const page = await pdfDoc.getPage(pageNum);
        
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('Could not get canvas context');
        }
        
        // Calculate viewport size
        const viewport = page.getViewport({ scale });
        
        // Set canvas dimensions
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        
        // Render PDF page into canvas context
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };
        
        await page.render(renderContext).promise;
      } catch (err) {
        console.error('Error rendering page:', err);
        setError(err instanceof Error ? err.message : 'Failed to render page');
      }
    };
  
    const nextPage = () => {
      if (pageNum < numPages) {
        setPageNum(pageNum + 1);
      }
    };
  
    const prevPage = () => {
      if (pageNum > 1) {
        setPageNum(pageNum - 1);
      }
    };
  
    const zoomIn = () => {
      setScale(prevScale => Math.min(prevScale + 0.2, 3));
    };
  
    const zoomOut = () => {
      setScale(prevScale => Math.max(prevScale - 0.2, 0.6));
    };
  
    return (
      <div className="flex flex-col h-full">
        {/* Document header */}
        <div className="flex items-center justify-between bg-purple-500/10 border-b border-purple-500/20 p-4">
          <h3 className="text-lg font-medium text-purple-100 truncate">
            {filename || `Document #${documentId}`}
          </h3>
          
          {!loading && !error && (
            <div className="flex items-center space-x-2">
              <button onClick={zoomOut} className="p-2 text-purple-300 hover:bg-purple-500/20 rounded">
                <ZoomOut className="w-5 h-5" />
              </button>
              <span className="text-purple-300">{Math.round(scale * 100)}%</span>
              <button onClick={zoomIn} className="p-2 text-purple-300 hover:bg-purple-500/20 rounded">
                <ZoomIn className="w-5 h-5" />
              </button>
              <a 
                href={`${config.API_URL}/api/documents/${documentId}/download`}
                download
                className="p-2 text-purple-300 hover:bg-purple-500/20 rounded ml-2"
              >
                <Download className="w-5 h-5" />
              </a>
            </div>
          )}
        </div>
        
        {/* PDF Viewer */}
        <div 
          ref={containerRef}
          className="flex-1 overflow-auto bg-black/50 flex items-center justify-center relative"
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center">
              <Loader className="w-8 h-8 text-purple-400 animate-spin" />
              <p className="mt-2 text-purple-300">Loading PDF...</p>
            </div>
          ) : error ? (
            <div className="text-center p-4">
              <p className="text-red-300">{error}</p>
              <button 
                onClick={loadPdf}
                className="mt-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              <canvas ref={canvasRef} className="shadow-lg" />
              
              {/* Page navigation */}
              {numPages > 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center bg-black/60 rounded-full px-4 py-2">
                  <button 
                    onClick={prevPage} 
                    disabled={pageNum <= 1}
                    className="p-1 text-purple-300 hover:bg-purple-500/20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="mx-2 text-purple-100">
                    {pageNum} / {numPages}
                  </span>
                  <button 
                    onClick={nextPage} 
                    disabled={pageNum >= numPages}
                    className="p-1 text-purple-300 hover:bg-purple-500/20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };
  
  export default PDFViewer;